import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  Badge,
  Button,
  cn,
  Skeleton,
  Tabs,
  TabsList,
  TabsTrigger,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  addDays,
  addMonths,
  endOfMonth,
  format,
  getDay,
  startOfMonth,
} from "date-fns";
import {
  CalendarDaysIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from "lucide-react";
import { useMemo, useState } from "react";
import type {
  EventCalendarItemDTO,
  EventCalendarLayerDTO,
  EventCalendarTbdItemDTO,
} from "../../domain/contracts/event-calendar";
import { useEventsCalendarQuery } from "../api/queries";
import {
  buildEventCalendarQuery,
  EVENT_CALENDAR_STATUS_BADGE,
  EVENT_CALENDAR_STATUS_LABELS,
  monthRange,
  parseCalendarMonth,
  toYmd,
} from "../lib/event-calendar-ui";
import { CalendarDayDrawer } from "./CalendarDayDrawer";

const WEEKDAY_LABELS = [
  "ວັນອາທິດ",
  "ວັນຈັນ",
  "ວັນອັງຄານ",
  "ວັນພຸດ",
  "ວັນພະຫັດ",
  "ວັນສຸກ",
  "ວັນເສົາ",
];

type PerformanceEventCalendarProps = {
  filters?: FilterConditionDTO[];
  calendarMonth?: string;
  calendarLayer: EventCalendarLayerDTO;
  onCalendarMonthChange: (month: string) => void;
  onCalendarLayerChange: (layer: EventCalendarLayerDTO) => void;
};

function groupItemsByDate(items: EventCalendarItemDTO[]) {
  const map = new Map<string, EventCalendarItemDTO[]>();
  for (const item of items) {
    const list = map.get(item.date) ?? [];
    list.push(item);
    map.set(item.date, list);
  }
  return map;
}

function CalendarDayItemsList({
  items,
  onOpenEvent,
}: {
  items: EventCalendarItemDTO[];
  onOpenEvent: (eventId: string) => void;
}) {
  if (items.length === 0) {
    return <p className="text-muted-foreground text-sm">ບໍ່ມີງານໃນວັນນີ້</p>;
  }

  return (
    <ul className="space-y-2">
      {items.map((item) => (
        <li key={item.id}>
          <button
            type="button"
            className="flex w-full items-start gap-2 rounded-lg border px-3 py-2 text-left transition-colors hover:bg-muted/40"
            onClick={() => onOpenEvent(item.eventId)}
          >
            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-sm">{item.title}</p>
              <p className="mt-0.5 text-muted-foreground text-xs">
                {item.startTime ? `${item.startTime} · ` : ""}
                {item.categoryName ?? "—"}
                {item.roomName ? ` · ${item.roomName}` : ""}
              </p>
            </div>
            <Badge
              variant="outline"
              className={cn(
                "shrink-0",
                EVENT_CALENDAR_STATUS_BADGE[item.status],
              )}
            >
              {EVENT_CALENDAR_STATUS_LABELS[item.status] ?? item.status}
            </Badge>
          </button>
        </li>
      ))}
    </ul>
  );
}

function TbdEventsPanel({
  items,
  onOpenEvent,
}: {
  items: EventCalendarTbdItemDTO[];
  onOpenEvent: (eventId: string) => void;
}) {
  if (items.length === 0) return null;

  return (
    <div className="rounded-xl border border-dashed bg-muted/10 p-4">
      <h3 className="mb-2 font-semibold text-sm">ຍັງບໍ່ກຳນົດວັນ ({items.length})</h3>
      <ul className="space-y-2">
        {items.map((item) => (
          <li key={item.eventId}>
            <button
              type="button"
              className="flex w-full items-center justify-between gap-2 rounded-lg border bg-background px-3 py-2 text-left text-sm hover:bg-muted/40"
              onClick={() => onOpenEvent(item.eventId)}
            >
              <span className="truncate font-medium">{item.title}</span>
              <Badge variant="outline">
                {EVENT_CALENDAR_STATUS_LABELS[item.status] ?? item.status}
              </Badge>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PerformanceEventCalendar({
  filters,
  calendarMonth,
  calendarLayer,
  onCalendarMonthChange,
  onCalendarLayerChange,
}: PerformanceEventCalendarProps) {
  const nav = useNavigate({ from: "/app/performance/events" });
  const monthDate = parseCalendarMonth(calendarMonth);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [daySheetOpen, setDaySheetOpen] = useState(false);

  const range = useMemo(() => monthRange(monthDate), [monthDate]);
  const query = useMemo(
    () =>
      buildEventCalendarQuery({
        start: range.start,
        end: range.end,
        layer: calendarLayer,
        filters,
      }),
    [range.end, range.start, calendarLayer, filters],
  );

  const calendarQuery = useEventsCalendarQuery(query);
  const itemsByDate = useMemo(
    () => groupItemsByDate(calendarQuery.data?.items ?? []),
    [calendarQuery.data?.items],
  );

  const calendarDays = useMemo(() => {
    const start = startOfMonth(monthDate);
    const end = endOfMonth(monthDate);
    const firstDow = getDay(start);
    const cells: { date: Date; isCurrentMonth: boolean }[] = [];
    for (let i = 0; i < firstDow; i++) {
      cells.push({ date: addDays(start, i - firstDow), isCurrentMonth: false });
    }
    for (let d = 1; d <= end.getDate(); d++) {
      cells.push({
        date: new Date(monthDate.getFullYear(), monthDate.getMonth(), d),
        isCurrentMonth: true,
      });
    }
    while (cells.length < 42) {
      cells.push({
        date: addDays(cells[cells.length - 1]!.date, 1),
        isCurrentMonth: false,
      });
    }
    return cells;
  }, [monthDate]);

  const todayYmd = toYmd(new Date());
  const selectedItems = selectedDate ? (itemsByDate.get(selectedDate) ?? []) : [];

  const openEvent = (eventId: string) => {
    nav({ to: "/app/performance/events/$id", params: { id: eventId } });
  };

  const openDaySheet = (dateStr: string) => {
    setSelectedDate(dateStr);
    setDaySheetOpen(true);
  };

  const closeDaySheet = (open: boolean) => {
    setDaySheetOpen(open);
    if (!open) setSelectedDate(null);
  };

  const shiftMonth = (delta: number) => {
    const next = addMonths(monthDate, delta);
    onCalendarMonthChange(
      `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, "0")}`,
    );
    setDaySheetOpen(false);
    setSelectedDate(null);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3 px-2">
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => shiftMonth(-1)}
          >
            <ChevronLeftIcon className="size-4" />
          </Button>
          <div className="min-w-36 text-center font-semibold">
            {format(monthDate, "MMMM yyyy")}
          </div>
          <Button
            type="button"
            variant="outline"
            size="icon"
            className="size-8"
            onClick={() => shiftMonth(1)}
          >
            <ChevronRightIcon className="size-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              const now = new Date();
              onCalendarMonthChange(
                `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`,
              );
              setDaySheetOpen(false);
              setSelectedDate(null);
            }}
          >
            ເດືອນນີ້
          </Button>
        </div>

        <Tabs
          value={calendarLayer}
          onValueChange={(value) =>
            onCalendarLayerChange(value as EventCalendarLayerDTO)
          }
        >
          <TabsList>
            <TabsTrigger value="events">ງານ</TabsTrigger>
            <TabsTrigger value="shows">ສະແດງ</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div className="overflow-x-auto rounded-xl border bg-card">
        {calendarQuery.isLoading ? (
          <div className="p-4">
            <Skeleton className="h-[420px] w-full" />
          </div>
        ) : (
          <table className="w-full min-w-[760px] table-fixed border-collapse text-sm">
            <colgroup>
              {WEEKDAY_LABELS.map((label) => (
                <col key={label} className="w-[14.28%]" />
              ))}
            </colgroup>
            <thead>
              <tr>
                {WEEKDAY_LABELS.map((label) => (
                  <th
                    key={label}
                    className="border-b bg-muted/40 p-2 text-center font-medium text-muted-foreground text-xs"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Array.from({ length: 6 }, (_, row) => (
                <tr key={row} className="h-32">
                  {calendarDays.slice(row * 7, row * 7 + 7).map(({ date, isCurrentMonth }) => {
                    const dateStr = toYmd(date);
                    const dayItems = itemsByDate.get(dateStr) ?? [];
                    const isToday = dateStr === todayYmd;
                    const isSelected = dateStr === selectedDate && daySheetOpen;
                    return (
                      <td
                        key={dateStr}
                        className={cn(
                          "h-32 border-b border-r p-0 align-top last:border-r-0",
                          isCurrentMonth ? "bg-background" : "bg-muted/20 text-muted-foreground",
                          isSelected && "ring-2 ring-primary ring-inset",
                        )}
                      >
                        <div className="flex h-full flex-col p-2">
                          <button
                            type="button"
                            className={cn(
                              "mb-2 flex size-7 shrink-0 items-center justify-center rounded-full text-xs",
                              isToday && "bg-destructive font-semibold text-destructive-foreground",
                              isSelected && !isToday && "bg-primary text-primary-foreground",
                            )}
                            onClick={() => isCurrentMonth && openDaySheet(dateStr)}
                            disabled={!isCurrentMonth}
                          >
                            {date.getDate()}
                          </button>
                          <div className="min-h-0 flex-1 space-y-1 overflow-hidden">
                            {dayItems.slice(0, 3).map((item) => (
                              <button
                                key={item.id}
                                type="button"
                                className={cn(
                                  "block w-full truncate rounded px-1.5 py-0.5 text-left text-[11px] leading-tight",
                                  EVENT_CALENDAR_STATUS_BADGE[item.status],
                                )}
                                title={item.title}
                                onClick={() => openEvent(item.eventId)}
                              >
                                {item.startTime ? `${item.startTime} ` : ""}
                                {item.title}
                              </button>
                            ))}
                            {dayItems.length > 3 ? (
                              <button
                                type="button"
                                className="text-[11px] text-primary"
                                onClick={() => openDaySheet(dateStr)}
                              >
                                +{dayItems.length - 3} ອີກ
                              </button>
                            ) : null}
                          </div>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TbdEventsPanel
        items={calendarQuery.data?.tbdEvents ?? []}
        onOpenEvent={openEvent}
      />

      <CalendarDayDrawer
        open={daySheetOpen}
        onOpenChange={closeDaySheet}
        date={selectedDate}
      >
        <CalendarDayItemsList items={selectedItems} onOpenEvent={openEvent} />
      </CalendarDayDrawer>

      <div className="flex flex-wrap items-center gap-3 px-2 text-muted-foreground text-xs">
        <span className="inline-flex items-center gap-1">
          <CalendarDaysIcon className="size-3.5" />
          {calendarLayer === "shows"
            ? "ສະແດງຕາມ performance_event_shows"
            : "ງານຕາມ eventDate → endDate"}
        </span>
        {Object.entries(EVENT_CALENDAR_STATUS_LABELS).map(([status, label]) => (
          <span key={status} className="inline-flex items-center gap-1">
            <span
              className={cn(
                "size-2 rounded-full",
                EVENT_CALENDAR_STATUS_BADGE[status],
              )}
            />
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}
